import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { Backdrop, Tooltip, useTheme } from "@mui/material";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiBell } from "react-icons/fi";

import CustomPaginationWithoutCount from "components/CustomPaginationWithoutCount/CustomPaginationWithoutCount";
import ALoader from "views/pages/manage/common/a-loader";
import APopover from "../../../../common/a-popover";
import ADataGrid from "views/pages/manage/common/data-grid/ADataGrid";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";
import { formattedPrice } from "../../../../unAssigned/collectPayment/utils";

import { REACT_ICONS_STYLE, printReceipt } from "./constants";
import TableMenuActions from "./TableMenuActions";
import { AppIdPopover, BUPopover } from "./ColumnPopovers";
import { EyeIcon } from "./EyeIcon";
import { CopyIcon } from "./CopyIcon";

import NoReceiptsFound from "./NoReceiptsFound";
import BulkPrintReceipt from "./BulkPrintReceipt";
import SendNotification from "./SendNotification";

import {
  PrintIcon,
  EditIcon,
  getBuIdByDisplayValue,
} from "./viewReceipt/constant";
import ModifyPayment from "./viewReceipt/modifyPayment/ModifyPayment";
import {
  checkPaymentModeApprovalStatus,
  getReceiptDetail,
} from "./viewReceipt/utils";
import { usePaymentCollectionReceiptContext } from "./context";

const ManageReceiptsList = ({
  rows,
  isLoading,
  isLastPage,
  onPageChange,
  currentPage,
  businessAreaOptions,
  isCentralizedAdm,
  opportunityOwnerId
}) => {
  const BULK_PRINT_LIMIT = 10;
  const theme = useTheme();
  const history = useHistory();

  const [selectionModel, setSelectionModel] = useState([]);
  const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false);
  const [sendNotif, setSendNotif] = useState({ isOpen: false, details: null });

  const [isModifyPaymentOpen, setModifyPaymentOpen] = useState(false);
  const [receitDetail, setReceiptDeatail] = useState([]);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [receiptLoader, setReceiptLoader] = useState(false);

  const [toolTipText, setToolTipText] = useState("Copy");

  const formattedDateTime = (timestamp) =>
    moment(timestamp).format("DD/MM/YYYY");

  const handlePsidClick = (psid) => {
    window.open(
      history.createHref({
        pathname: `/admin/edp/studentDetails/${psid}`,
      }),
      "_blank"
    );
  };

  const MENU_ITEMS = [
    {
      icon: <PrintIcon color={"#0F0F0F"} />,
      text: "Print Receipt",
      handleClick: async (rowData) => {
        setIsGeneratingReceipt(true);

        // single receipt
        await printReceipt({
          receipts: [rowData],
        });
        setIsGeneratingReceipt(false);
      },
    },
    {
      icon: <EditIcon />,
      text: "Modify payment method",
      handleClick: (rowData) => {
        getDetail(rowData);
      },
      isDisabled : ({applicationId}) => (!applicationId || !applicationId?.trim())
    },
    {
      icon: (
        <FiBell style={{ width: "24px", height: "24px", color: `#0F0F0F` }} />
      ),
      text: "Send Notification",
      handleClick: (rowData) => {
        setSendNotif({ isOpen: true, details: {...rowData ,opportunityOwnerId} });
      },
    },
  ];

  const columns = [
    new AGridColDef("psid", "PSID")
      .setMinWidth(150)
      .renderCellComponent((params) => (
        <div>
          <span
            className="pointer mr-2"
            onClick={() => handlePsidClick(params?.value)}
          >
            {params?.value}
          </span>
          <Tooltip title={toolTipText}>
            <span
              onClick={() => {
                navigator.clipboard.writeText(params.value);
                setToolTipText("Copied!");

                setTimeout(() => {
                  setToolTipText("Copy");
                }, 1500);
              }}
              className="pointer"
            >
              <CopyIcon />
            </span>
          </Tooltip>
        </div>
      )),

    new AGridColDef("acknowledgementId", "Ack ID")
      .setMinWidth(80)
      .setFlex(0.5)
      .renderCellComponent((params) => (
        <div>{params?.value?.trim() || "-"}</div>
      )),

    new AGridColDef("businessArea", "BU")
      .setWidth(50)
      .renderCellComponent((params) => (
        <div>
          <span>{params.value}</span>&nbsp;
          <span>
            <BUPopover
              businessArea={params.value}
              businessAreaOptions={businessAreaOptions}
            />
          </span>
        </div>
      )),

    new AGridColDef("applicationId", "Application ID")
      .setMinWidth(110)
      .renderCellComponent((params) => (
        <div className="m-auto">
          <span>{params?.value.trim() || "-"}</span>&nbsp;
          {params?.value?.trim() && (
            <span>
              <AppIdPopover
               rowData ={params?.row}
              />
            </span>
          )}
        </div>
      )),

    new AGridColDef("studentName", "Student Name")
      .setWidth(100)
      .renderCellComponent((params) => (
        <Tooltip title={params?.value}>
          <div className="ellipsis-text-container">{params?.value ?? "-"}</div>
        </Tooltip>
      )),

    new AGridColDef("receiptId", "PSERP Receipt ID")
      .setMinWidth(180)
      .renderCellComponent((params) =>
        params?.value ? (
          <Tooltip title={params?.value}>
          <div>{params?.value}</div>
          </Tooltip>
        ) : (
          <div class="large-semi-bold label-color">
            <span style={{ color: "rgb(210, 152, 34)" }}>In progress</span>
          </div>
        )
      ),

    new AGridColDef("receiptType", "Receipt Type").renderCellComponent(
      (params) =>
        params?.value?.trim() ? (
          <Tooltip title={params?.value}>
            <div className="ellipsis-text-container">{params?.value}</div>
          </Tooltip>
        ) : (
          <div className="m-auto">-</div>
        )
    ),

    new AGridColDef("paymentModes", "Paymode").renderCellComponent((params) =>
      params?.value?.trim() ? (
        <Tooltip title={params?.value}>
          <div className="ellipsis-text-container">{params?.value}</div>
        </Tooltip>
      ) : (
        <div className="m-auto">-</div>
      )
    ),

    new AGridColDef("totalAmountSf", "Amount")
      .setWidth(100)
      .renderCellComponent((params) => {
        const amount = formattedPrice(params.value, false);
        const isLessThanZero = Number(params.value) < 0;

        return (
          <span
            style={{
              color: isLessThanZero ? "#DC2804" : "#05945B",
              fontFamily: "Gilroy-Medium",
              fontWeight: "700",
            }}
          >
            ₹ {amount}
          </span>
        );
      }),

    new AGridColDef("receiptDate", "Receipt Date")
      .setMinWidth(80)
      .renderCellComponent((params) =>
        params?.value ? (
          <Tooltip title={formattedDateTime(params?.value)}>
            <div className="ellipsis-text-container">
              {formattedDateTime(params?.value)}
            </div>
          </Tooltip>
        ) : (
          <div className="m-auto">-</div>
        )
      ),

    new AGridColDef("Actions", "Action", false)
      // .setWidth(40)
      .columnAlign("right")
      .renderCellComponent((params) => {
        const {
          acknowledgementId,
          applicationId,
          businessArea,
          psid,
          receiptId,
        } = params.row;
        return (
          <>
            <Tooltip title="View">
              <span
                onClick={() => {
                  const url = `${
                    window.location.origin
                  }/#/admin/paymentCollection/viewReceipt/${psid}?acknowledgementId=${
                    acknowledgementId === "N/A" ? "" : acknowledgementId
                  }&businessArea=${encodeURIComponent(
                    businessArea
                  )}&applicationId=${encodeURIComponent(
                    applicationId
                  )}&receiptId=${encodeURIComponent(receiptId)}
                   &opportunityOwnerId=${encodeURIComponent(opportunityOwnerId)}
                  `;

                  // Open the new tab with the constructed URL
                  window.open(url, "_blank").focus();
                }}
                style={{
                  color: " #00B0F5",
                  cursor: "pointer",
                }}
              >
                <EyeIcon />
              </span>
            </Tooltip>
            &nbsp;&nbsp;&nbsp;
            <APopover
              buttonComponent={(onClick, styles) => (
                <Tooltip title="More Actions">
                  <span onClick={onClick}>
                    <IconButtonWrapper>
                      <BsThreeDotsVertical style={REACT_ICONS_STYLE} />
                    </IconButtonWrapper>
                  </span>
                </Tooltip>
              )}
              menuComponent={(closeForm) => (
                <TableMenuActions
                  handleClose={closeForm}
                  menuItems={MENU_ITEMS}
                  rowData={params?.row}
                  isCentralizedAdm={isCentralizedAdm}
                  opportunityOwnerId ={opportunityOwnerId}
                />
              )}
              sx={{
                "& .MuiPaper-root": {
                  border: `1px solid #E2E2E2`,
                  boxShadow: "0px 3px 4px 0px #00000014 !important",
                  borderRadius: "8px",
                },
              }}
              // transformOriginVertical="center"
              // transformOriginHorizontal="left"
            />
          </>
        );
      }),
  ];

  const { setStudentBasicDetails } = usePaymentCollectionReceiptContext();
  const getDetail = async (rowData) => {
    setReceiptLoader(true);
    let amountPaid = 0;
    const res = await getReceiptDetail(rowData);
    res.paymentModeBreakupDTOList.forEach((item) => {
      amountPaid = amountPaid + item?.paidAmount;
    });
    const buId = await getBuIdByDisplayValue(rowData?.businessArea);
    setStudentBasicDetails((prev) => ({
      ...prev,
      applicationId: rowData?.applicationId,
      psid: rowData?.psid,
      buId: buId,
      receiptId: res?.receiptNumber,
      receiptDate: res?.receiptDate,
      totalPaidAmount: amountPaid,
      businessArea: rowData?.businessArea,
      term: res?.term,
      acknowledgementId: rowData?.acknowledgementId,
    }));
    setTotalPaidAmount(amountPaid);
    setReceiptDeatail(
      res?.paymentModeBreakupDTOList.map((item, index) => ({
        ...item,
        id: index,
      }))
    );
    await checkPaymentModeApprovalStatus(
      res?.receiptNumber,
      setModifyPaymentOpen
    );
    setReceiptLoader(false);
  };

  return (
    <div style={{ height: "100%" }}>
      <ADataGrid
        rows={rows}
        columns={columns}
        rowId={(row) => row.rowNumber}
        hidePagination={true}
        loading={isLoading}
        emptyRowPlaceholder={() => (
          <NoReceiptsFound isCentralizedAdm={isCentralizedAdm} />
        )}
        checkboxSelection
        isRowSelectable={(params) => {
          if (params?.row?.receiptId && params?.row?.receiptId.trim())
            return true;
          return false;
        }}
        selectionModel={selectionModel}
        onSelectionModelChange={(newSelectionModel) => {
          // max 10 rows can be selected for bulk print receipt
          if (newSelectionModel.length > BULK_PRINT_LIMIT)
            setSelectionModel(newSelectionModel.slice(0, BULK_PRINT_LIMIT));
          else setSelectionModel(newSelectionModel);
        }}
        disableSelectionOnClick
        hideFooterSelectedRowCount={true}
        height={isCentralizedAdm ? "58%" : "78%"}
        sx={{
          minHeight: "85%",
          [theme.breakpoints.up("lg")]: {
            minHeight: "90%",
          },
        }}
      />

      <CustomPaginationWithoutCount
        currentPage={currentPage}
        onPageChange={onPageChange}
        isLastPage={isLastPage}
        noOfCurrentPageRecords={rows.length}
      />

      <BulkPrintReceipt
        rows={rows}
        selectionModel={selectionModel}
        setSelectionModel={setSelectionModel}
        setIsGeneratingReceipt={setIsGeneratingReceipt}
      />

      {sendNotif.isOpen && (
        <SendNotification
          open={sendNotif.isOpen}
          receiptDetails={sendNotif.details}
          handleCloseDialog={() => {
            setSendNotif({ isOpen: false, details: null });
          }}
          // isGeneratingReceipt={isGeneratingReceipt}
          setIsGeneratingReceipt={setIsGeneratingReceipt}
        />
      )}

      {!receiptLoader && (
        <ModifyPayment
          isOpen={isModifyPaymentOpen}
          setIsOpen={setModifyPaymentOpen}
          rowList={receitDetail}
          totalPaidAmount={totalPaidAmount}
        />
      )}

      {(isGeneratingReceipt || receiptLoader) && (
        <Backdrop
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 1300,
          })}
          open={isGeneratingReceipt || receiptLoader}
        >
          <ALoader />
        </Backdrop>
      )}
    </div>
  );
};

export default ManageReceiptsList;
